import { EXTERNAL_ROUTES } from '@/constants';
import { formatCurrencyValue } from '@/modules/common/lib/formatters';
import { ICohort, ICourseWithEducationProvider } from '@/types/course';
import {
  CalendarTodayOutlined,
  ComputerOutlined,
  LabelOutlined,
  ScheduleOutlined,
} from '@mui/icons-material';
import { Tooltip, TooltipProps, Typography } from '@mui/material';
import pluralize from 'pluralize';

type CourseCohortDetailsTooltipProps = Omit<TooltipProps, 'title'> & {
  course: Pick<
    ICourseWithEducationProvider,
    'level' | 'categories' | 'cohorts' | 'name' | 'educationProvider' | '_id'
  >;
  cohort: ICohort;
};

export function CourseCohortDetailsTooltip({
  course,
  cohort,
  children,
  ...props
}: CourseCohortDetailsTooltipProps) {
  const { duration } = cohort;
  const durationLabel = duration
    ? pluralize(duration.type ?? '', duration.quantity ?? 0, true)
    : null;
  const coursePrice =
    Number.isInteger(cohort?.cost?.amount) &&
    cohort?.cost?.currency &&
    formatCurrencyValue(cohort.cost.amount!, cohort.cost.currency);

  return (
    <Tooltip
      {...props}
      placement="top-start"
      title={
        <div className="grid grid-cols-2 gap-y-4 gap-x-6 p-2">
          <header className="col-span-2 flex flex-col gap-1">
            <Typography
              className="text-mydra-purple font-semibold text-ellipsis overflow-hidden"
              variant="caption"
            >
              <a
                className="cursor-pointer hover:underline"
                href={EXTERNAL_ROUTES.MARKETPLACE_COURSE_URL.replace(
                  ':id',
                  course._id!,
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                {course.name}
              </a>
            </Typography>
            <Typography className="text-xs" variant="caption">
              {course.educationProvider?.name}
            </Typography>
            {coursePrice && (
              <Typography className="text-xs" variant="caption">
                {coursePrice}
              </Typography>
            )}
          </header>
          <div>
            <p className="font-semibold flex items-center gap-1 text-secondary">
              <CalendarTodayOutlined className="w-4 h-4" />
              Start date:
            </p>
            <p className="capitalize">{cohort?.startDate}</p>
          </div>
          <div>
            <p className="font-semibold flex items-center gap-1 text-secondary">
              <CalendarTodayOutlined className="w-4 h-4" />
              End date:
            </p>
            <p className="capitalize">{cohort?.endDate}</p>
          </div>
          <div>
            <p className="font-semibold flex items-center gap-1 text-secondary">
              <ComputerOutlined className="w-4 h-4" />
              Location:
            </p>
            <p className="capitalize">{cohort?.location}</p>
          </div>
          <div>
            <p className="font-semibold flex items-center gap-1 text-secondary">
              <LabelOutlined className="w-4 h-4" />
              Class type:
            </p>
            <p className="capitalize">{cohort?.classType}</p>
          </div>
          <div>
            <p className="font-semibold flex items-center gap-1 text-secondary">
              <ScheduleOutlined className="w-4 h-4" />
              Format:
            </p>
            <p className="capitalize">{cohort?.format}</p>
          </div>
          <div>
            <p className="font-semibold flex items-center gap-1 text-secondary">
              <ScheduleOutlined className="w-4 h-4" />
              Duration:
            </p>
            <p className="capitalize">{durationLabel}</p>
          </div>
        </div>
      }
    >
      {children}
    </Tooltip>
  );
}
